import React from 'react';
import { Check, Monitor, Moon, Sun } from 'lucide-react';
import { useT } from '../../../../lib/i18n';
import { usePreferences } from '../../preferences/PreferencesContext';
import { ErrorBanner } from '../../ui';
import { MiniDashboard, PreviewRow } from '../components/Previews';
import { Card, ChoiceCards, SectionHead } from '../components/SettingsUI';
import useAutoSave from './useAutoSave';

export const ACCENTS = [
  ['green', '#16a34a'], ['blue', '#2563eb'], ['purple', '#9333ea'], ['orange', '#f59e0b'], ['red', '#e11d48'],
  ['pink', '#ec4899'], ['teal', '#14b8a6'], ['indigo', '#6366f1'], ['slate', '#475569'],
];
const Lines = ({ n }) => (
  <span className="st-lines">{Array.from({ length: 3 }).map((_, i) => <i key={i} style={{ marginBottom: n }} />)}</span>
);

/** Settings › Appearance: theme, accent colour, font size and spacing, each applied straight away. */
export default function AppearanceSection() {
  const { t } = useT();
  const { dark } = usePreferences();
  const { values, set, error } = useAutoSave('appearance');
  const accent = (ACCENTS.find(([k]) => k === values.accent) || ACCENTS[0])[1];

  return (
    <>
      <SectionHead title={t('Appearance')} subtitle={t('Customize how TeleCalX looks and feels for you.')} />
      <ErrorBanner message={error} />
      <Card title={t('Theme')} subtitle={t('Choose your preferred theme for the dashboard.')}>
        <ChoiceCards name={t('Theme')} layout="column" value={values.theme} onChange={(theme) => set({ theme })} options={[
          { value: 'light', glyph: <Sun size={26} color="#f59e0b" />, label: t('Light Mode'), sub: t('Clean and bright interface') },
          { value: 'dark', glyph: <Moon size={26} color="#2563eb" />, label: t('Dark Mode'), sub: t('Easy on the eyes') },
          { value: 'system', glyph: <Monitor size={26} color="#1d4fa8" />, label: t('System Default'), sub: t('Follow your device settings') },
        ]} />
      </Card>

      <Card title={t('Color Accent')} subtitle={t('Choose a color theme for highlights and buttons.')}>
        <div className="st-swatches" role="radiogroup" aria-label={t('Color Accent')}>
          {ACCENTS.map(([key, color]) => (
            <button type="button" key={key} role="radio" aria-checked={values.accent === key} aria-label={t(key)} title={t(key)}
              className={`st-swatch ${values.accent === key ? 'on' : ''}`} style={{ '--sw': color }} onClick={() => set({ accent: key })}>
              {values.accent === key && <Check size={15} strokeWidth={3} />}
            </button>
          ))}
        </div>
      </Card>

      <Card title={t('Font Size')} subtitle={t('Adjust the text size for better readability.')}>
        <ChoiceCards name={t('Font Size')} value={values.font_size} onChange={(font_size) => set({ font_size })} options={[
          { value: 'small', glyph: 'A', glyphStyle: { fontSize: 18 }, label: t('Small'), sub: t('Compact view') },
          { value: 'medium', glyph: 'A', glyphStyle: { fontSize: 24 }, label: t('Medium'), sub: t('Default size') },
          { value: 'large', glyph: 'A', glyphStyle: { fontSize: 30 }, label: t('Large'), sub: t('Larger text for easy reading') },
        ]} />
      </Card>

      <Card title={t('Layout Density')} subtitle={t('Control the spacing of elements on the dashboard.')}>
        <ChoiceCards name={t('Layout Density')} value={values.density} onChange={(density) => set({ density })} options={[
          { value: 'compact', glyph: <Lines n={2} />, label: t('Compact'), sub: t('Show more content') },
          { value: 'comfortable', glyph: <Lines n={4} />, label: t('Comfortable'), sub: t('Balanced spacing') },
          { value: 'spacious', glyph: <Lines n={6} />, label: t('Spacious'), sub: t('More space and larger buttons') },
        ]} />
      </Card>

      <Card title={t('Preview')} subtitle={t('See how the dashboard will look with your settings.')}>
        <PreviewRow items={[
          { key: 'light', drawing: <MiniDashboard variant="light" accent={accent} />, label: values.theme === 'light' ? t('Light Mode (Current)') : t('Light Mode'), current: values.theme === 'light', onClick: () => set({ theme: 'light' }) },
          { key: 'dark', drawing: <MiniDashboard variant="dark" accent={accent} />, label: values.theme === 'dark' ? t('Dark Mode (Current)') : t('Dark Mode'), current: values.theme === 'dark', onClick: () => set({ theme: 'dark' }) },
          { key: 'system', drawing: <MiniDashboard variant={dark ? 'dark' : 'system'} accent={accent} />, label: values.theme === 'system' ? t('System Default (Current)') : t('System Default'), current: values.theme === 'system', onClick: () => set({ theme: 'system' }) },
        ]} />
      </Card>
    </>
  );
}
